import { clstx } from '@payaca/utilities/cvaMerge';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from '@tiptap/markdown';
import { useEditor } from '@tiptap/react';
import { useEffect, useMemo, useRef } from 'react';

import {
  DEFAULT_INLINE_TOOLBAR,
  DEFAULT_TOOLBAR,
  type EditorToolbarItem,
} from './editorActions';
import { baseEditorExtensions } from './editorExtensions';
import EditorShell from './EditorShell';

import type { JSX } from 'react';

/**
 * Subset of the master `EditorToolbarItem` union that the markdown editor
 * supports. `underline` is excluded — the markdown schema cannot represent
 * an underline mark, so the type prevents callers from enabling it.
 */
export type MarkdownToolbarItem = Exclude<EditorToolbarItem, 'underline'>;

export interface MarkdownTextareaProps {
  value: string;
  onChange?: (markdown: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  readOnly?: boolean;
  /**
   * Render the editor inline with no surrounding frame (no border/box) and no
   * padding, so it sits directly on the page. Formatting is surfaced via a
   * bubble menu on text selection rather than a static toolbar. Has no effect
   * when `readOnly`.
   */
  borderless?: boolean;
  /**
   * Pass `false` to hide the formatting controls entirely. Pass an array to
   * customise which actions are offered — in the static toolbar by default, or
   * in the bubble menu when `borderless`. Defaults to `DEFAULT_TOOLBAR`
   * (`DEFAULT_INLINE_TOOLBAR` when `borderless`). An explicit array opts out of
   * that curation, so in `borderless` mode it may include document-level items
   * (e.g. `undo`/`redo`) that the inline default deliberately omits.
   */
  toolbar?: MarkdownToolbarItem[] | false;
  className?: string;
  /**
   * Accessible name for the editor's contenteditable region. Surfaces as the
   * accessible textbox name (assistive tech + role-based queries).
   */
  'aria-label'?: string;
}

const MarkdownTextarea = (props: MarkdownTextareaProps): JSX.Element => {
  const {
    value,
    onChange,
    onBlur,
    placeholder,
    readOnly = false,
    borderless = false,
    toolbar,
    className,
    'aria-label': ariaLabel,
  } = props;

  const toolbarItems =
    toolbar ?? (borderless ? DEFAULT_INLINE_TOOLBAR : DEFAULT_TOOLBAR);

  // Track the markdown we last emitted via onChange so the value-sync effect
  // can skip re-serialising when the parent simply echoes it back.
  const lastEmittedRef = useRef<string>(value ?? '');

  const extensions = useMemo(
    () => [
      ...baseEditorExtensions,
      Markdown,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    [placeholder]
  );

  const editor = useEditor({
    extensions,
    content: value ?? '',
    contentType: 'markdown',
    onUpdate: ({ editor }) => {
      const markdown = editor.getMarkdown();
      lastEmittedRef.current = markdown;
      onChange?.(markdown);
    },
    onBlur: () => onBlur?.(),
    editorProps: {
      attributes: {
        class: clstx(
          'tiptap-editor',
          'typography:prose focus:outline-none',
          !readOnly && !borderless ? 'p-3' : '',
          className
        ),
        // Tiptap's contenteditable doesn't get an implicit textbox role in
        // the accessibility tree; set it so the editor is discoverable
        // via role-based queries (assistive tech + tests).
        role: 'textbox',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
    editable: !readOnly,
  });

  // Toggle editability without rebuilding the editor — preserves undo history,
  // focus, and scroll position when readOnly flips at runtime.
  useEffect(() => {
    if (!editor) return;
    if (editor.isEditable !== !readOnly) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  // Keep the editor in sync when the parent passes a new `value` (e.g. form
  // reset). Skip when the new value is what we just emitted — that's the
  // controlled-component echo and re-serialising would be wasted work on the
  // typing hot path.
  useEffect(() => {
    if (!editor) return;
    const next = value ?? '';
    if (next === lastEmittedRef.current) return;
    if (editor.getMarkdown() === next) return;
    editor.commands.setContent(next, {
      contentType: 'markdown',
      emitUpdate: false,
    });
    lastEmittedRef.current = next;
  }, [editor, value]);

  return (
    <>
      <EditorShell
        editor={editor}
        readOnly={readOnly}
        toolbar={toolbarItems}
        borderless={borderless}
      />
      {!readOnly && !borderless && (
        <p className="text-sm text-gray-500">Shift+Enter for line break</p>
      )}
    </>
  );
};

export default MarkdownTextarea;
